<template>
  <application-layout v-if="hideNavigation" />
  <template v-else>
    <!-- eslint-disable-next-line vue/no-undef-components -->
    <app-wrapper
      :active-item="currentRouteName"
      :nav-items-map="navItemsMap"
      :workspace-options="workspaceOptions"
      :active-workspace="toWorkspaceOption(session.workspace)"
      :translations="translations"
      :help-menu-items="helpMenuItems"
      :subnav-title="sidebarConfig.title"
      :subnav-items="sidebarConfig.items"
      :subnav-action-tooltip="sidebarConfig.action?.tooltip || ''"
      :subnav-action-menu-items="sidebarConfig.action?.menuItems"
      :profile-menu-items="profileMenuItems"
      :hide-toggle-subnav-button="!!sidebarConfig?.hideToggleSubnavButton"
      :global-banners="globalBanners"
      :has-flag-manager="config.flagManager"
      :assets-override="assetsOverride"
      @nav-item-click="onNavigationClick"
      @subnav-item-click="sidebarConfig.handleSubnavItemClick"
      @subnav-item-action-click="sidebarConfig.handleSubnavItemActionClick"
      @subnav-extra-click="sidebarConfig.handleGroupActionClick"
      @help-menu-item-click="onHelpMenuItemClick"
      @workspace-click="onWorkspaceSwitch"
      @profile-menu-click="onProfileMenuItemClick"
      @subnav-action-click="sidebarConfig.action?.onClick"
      @banner-click="onBannerClick"
      @search-query-change="onSearchQueryChange"
      @search-result-select="onSearchResultSelect"
      @clear-assets-override="onClearAssetsOverride"
    />

    <!-- Renderless sidebar component for state management -->
    <component :is="sidebarComponent" v-if="sidebarComponent" @update:config="updateSidebarConfig" />

    <!-- Feature flags modal -->
    <zd-nav-flag-manager v-if="config.flagManager" />

    <!-- Modal for switching the account -->
    <switch-account-modal :show="showAccountSwitchModal" @close="showAccountSwitchModal = false" />

    <!-- Modal with keybord shortcuts preview -->
    <keyboard-shortcuts-modal :visible="showKeyboardShortcutsModal" @close="showKeyboardShortcutsModal = false" />

    <q-error-cat />
  </template>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import type { NavigationKey, WorkspaceOption } from '@klausapp/app-navigation/types'
import config from '@/config'
import analytics from '@/utils/analytics'
import i18n from '@/i18n'
import { session } from '@/composables/useSession'
import type { SessionWorkspace } from '@/types/session'
import type { WorkspaceItem } from '@/types/workspace'
import { useWorkspaceList } from '@/composables/useWorkspaceList'
import useProfileMenu from '@/composables/useProfileMenu'
import KeyboardShortcutsModal from '@/modules/shared/Navigation/components/KeyboardShortcutsModal.vue'
import SwitchAccountModal from '@/modules/shared/Navigation/components/SwitchAccountModal.vue'
import useHelpMenu from '@/composables/useHelpMenu'
import { useNavigationSearch } from '@/composables/useNavigationSearch'
import { useHelpMenuItems } from './composables/useHelpMenuItems'
import QErrorCat from './components/QErrorCat.vue'

import type { SidebarConfig } from './types'

import ApplicationLayout from './Application.vue'
import AppWrapper from './AppWrapper.vue'
import useNavigation from './useNavigation'
import ConversationsSidebar from './components/ConversationsSidebar.vue'
import ActivitySidebar from './components/ActivitySidebar.vue'
import CoachingSidebar from './components/CoachingSidebar.vue'
import SettingsSidebar from './components/SettingsSidebar.vue'
import DashboardSidebar from './components/DashboardSidebar.vue'
import UsersWorkspacesSidebar from './components/UsersWorkspacesSidebar.vue'
import TasksSidebar from './components/TasksSidebar.vue'
import ZdNavFlagManager from './components/NavLogo/ZdNavFlagManager.vue'
import { useConstructionLogo } from './composables/useConstructionLogo'
import useApplicationBanners from './composables/useApplicationBanners'

const translations = {
  no_results: i18n.t('pickers.workspaces.no_results'),
  search_placeholder: i18n.t('universal.search'),
  help_tooltip: i18n.t('modules.application.navbar.help'),
  search_no_results: i18n.t('components.command_palette.not_found'),
  'conversations.filters.delete_filter': i18n.t('conversations.filters.delete_filter'),
  'conversations.filters.clone_filter': i18n.t('conversations.filters.clone_filter'),
  'conversations.filters.edit_filter': i18n.t('conversations.filters.edit_filter'),
}

// Reactive state
const route = useRoute()
const router = useRouter()

// Composables
const { getRoute, navItemsMap, currentRouteName, getWorkspaceSwitchRoute } = useNavigation()
const [workspaces] = useWorkspaceList()
const { showAccountSwitchModal, profileMenuItems, onProfileMenuItemClick } = useProfileMenu()
const { helpMenuItems, onHelpMenuItemClick } = useHelpMenuItems()
const { showKeyboardShortcutsModal } = useHelpMenu()
const { globalBanners, onBannerClick } = useApplicationBanners()
const { searchResults, onSearchQueryChange, onSearchResultSelect } = useNavigationSearch()

// Dispatch search results to web component when they change
watch(searchResults, (results) => {
  if (results) {
    const customElement = document.querySelector('zui-navigation')
    if (customElement) {
      const event = new CustomEvent('zui-search-results', {
        detail: results,
        bubbles: true,
      })
      customElement.dispatchEvent(event)
    }
  }
})

const onWorkspaceSwitch = (workspaceId: number) => {
  if (workspaceId === session.workspace.id) return

  analytics.teamSelectionClicked(workspaceId)
  // Use workspace-agnostic route to avoid preserving old workspace's params (e.g., dashboard IDs)
  const targetRoute = getWorkspaceSwitchRoute()
  const currentUrl = new URL(`${window.location.origin}${router.resolve(targetRoute).href}`)
  currentUrl.searchParams.append('workspace', workspaceId.toString())

  window.location.href = currentUrl.toString()
}

const toWorkspaceOption = (workspace: SessionWorkspace | WorkspaceItem): WorkspaceOption => {
  const { id, name, color } = workspace
  return { id: id.toString(), name, color }
}

const workspaceOptions = computed(() => workspaces.value.map(toWorkspaceOption))
const hideNavigation = computed(() => !!route.meta?.hideNav)

const { assetsOverride, onClearAssetsOverride } = useConstructionLogo()

const getDefaultSidebarConfig = (): SidebarConfig => ({
  action: {
    tooltip: '',
    menuItems: [],
    onClick: () => {
      /* no-op */
    },
  },
  title: '',
  hideToggleSubnavButton: true,
  items: [],
  handleSubnavItemClick: () => {
    /* no-op */
  },
  handleSubnavItemActionClick: () => {
    /* no-op */
  },
  handleGroupActionClick: () => {
    /* no-op */
  },
})

const sidebarConfig = ref<SidebarConfig>(getDefaultSidebarConfig())

const sidebarComponents = {
  conversations: ConversationsSidebar,
  activity: ActivitySidebar,
  coaching: CoachingSidebar,
  tasks: TasksSidebar,
  dashboard: DashboardSidebar,
  settings: SettingsSidebar,
  'users-workspaces': UsersWorkspacesSidebar,
} as const

const sidebarComponent = computed(
  () => sidebarComponents[currentRouteName.value as keyof typeof sidebarComponents] || null,
)

const updateSidebarConfig = (config: SidebarConfig) => {
  sidebarConfig.value = config
}

// Reset to default config when no sidebar component is active
watch(
  sidebarComponent,
  (component) => {
    if (!component) {
      sidebarConfig.value = getDefaultSidebarConfig()
    }
  },
  { immediate: true },
)

const onNavigationClick = (key: NavigationKey): void => {
  const returnedRoute = getRoute(key)

  if (returnedRoute) {
    router.push(returnedRoute)
  }

  analytics.navbarEvent('Clicked navbar', key[0].toUpperCase() + key.slice(1))

  // Handle special case for conversations route
  if (key === 'conversations') {
    const { singleTicket, ...query } = route.query
    if (singleTicket) {
      router.replace({ query })
    }
  }
}
</script>
